import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { DepartmentService } from '../../services/department.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import {
    EquipmentCategory, EquipmentTypeDTO, LogSheet, LogSheetRow, MeterReadingItem, mntLabel,
} from '../../interfaces/maintenance';
import { Department } from '../../interfaces/department.interface';
import { useEquipmentGroups } from './useEquipmentGroups';

const cell = (basis: number) => ({ flex: `1 1 ${basis}px`, maxWidth: basis + 140, minWidth: 0 });

/** Today's calendar date in Dhaka (not the browser zone, not UTC). */
export const dhakaToday = (): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

/** Current Dhaka wall-clock time as HH:mm. */
export const dhakaNowTime = (): string =>
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dhaka', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date());

const fmt = (v?: number | null) => (v === undefined || v === null ? '-' : String(v));

/** A hint shown before saving; the server stays the authority. */
export const rowWarning = (r: LogSheetRow, raw: string, reset: boolean, backDated = false): string | null => {
    if (raw.trim() === '') return null;
    const v = Number(raw);
    if (!Number.isFinite(v)) return 'Not a number';
    if (r.kind !== 'GAUGE' && v < 0) return 'Cannot be negative';
    if (r.kind === 'CUMULATIVE' && !reset && !backDated && r.lastValue !== undefined && r.lastValue !== null && v < r.lastValue) {
        return `Lower than last reading ${r.lastValue}. Tick Reset if the counter was replaced or rolled over`;
    }
    if (r.kind === 'GAUGE') {
        if ((r.minValue !== undefined && r.minValue !== null && v < r.minValue) || (r.maxValue !== undefined && r.maxValue !== null && v > r.maxValue)) {
            return 'Outside the allowed range: it will be saved and managers will be told';
        }
    }
    return null;
};

/** Daily utility log sheet (maintenance L2): enter many meter readings at once, per-line results. */
const UtilityLogSheet: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [date, setDate] = useState(dhakaToday());
    const [category, setCategory] = useState<EquipmentCategory | ''>('');
    const [typeId, setTypeId] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [shift, setShift] = useState('');
    const [time, setTime] = useState(dhakaNowTime());
    const [timeTouched, setTimeTouched] = useState(false);
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const { groups } = useEquipmentGroups(institutionId);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [sheet, setSheet] = useState<LogSheet | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState<Record<string, string>>({});
    const [resets, setResets] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        MaintenanceService.listTypes(institutionId).then(setTypes).catch(e => toast.showApiError(e, 'Failed to load equipment types'));
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await MaintenanceService.getLogSheet(institutionId, { date, category, typeId, departmentId });
            setSheet(res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load the log sheet');
        } finally {
            setLoading(false);
        }
    }, [institutionId, date, category, typeId, departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    /** Typed values belong to the sheet that was on screen: drop them (and their errors) when the sheet changes. */
    const clearEntry = () => {
        setValues({});
        setResets({});
        setErrors({});
    };
    const changeDate = (d: string) => {
        clearEntry();
        setDate(d);
        setTimeTouched(false);
        setTime(d === dhakaToday() ? dhakaNowTime() : '12:00');
    };

    const entered = useMemo(
        () => (sheet?.rows ?? []).filter(r => (values[r.meterId] ?? '').trim() !== ''),
        [sheet, values],
    );

    const handleSave = async () => {
        const lines: MeterReadingItem[] = entered.map(r => {
            const line: MeterReadingItem = { meterId: r.meterId, value: Number(values[r.meterId]) };
            if (shift.trim()) line.shiftLabel = shift.trim();
            if (resets[r.meterId]) line.isReset = true;
            // Today with an untouched time: the server stamps "now". Otherwise the entered time of day in Dhaka
            // (default 12:00 for a back-dated sheet), so every line is a real instant, not a shared tie.
            if (date !== dhakaToday() || timeTouched) line.readAt = new Date(`${date}T${time || '12:00'}:00+06:00`).toISOString();
            return line;
        });
        if (lines.some(l => !Number.isFinite(l.value))) {
            toast.showError('One of the values is not a number');
            return;
        }
        try {
            setSaving(true);
            const res = await MaintenanceService.recordReadings(institutionId, lines);
            const nextErrors: Record<string, string> = {};
            const okIds = new Set<string>();
            res.items.forEach(it => {
                if (it.ok) okIds.add(it.meterId);
                else nextErrors[it.meterId] = it.message ?? 'Not saved';
            });
            setErrors(nextErrors);
            setValues(v => Object.fromEntries(Object.entries(v).filter(([k]) => !okIds.has(k))));
            setResets(v => Object.fromEntries(Object.entries(v).filter(([k]) => !okIds.has(k))));
            if (res.saved > 0) toast.showSuccess(`${res.saved} reading${res.saved === 1 ? '' : 's'} saved`);
            if (res.failed > 0) toast.showError(`${res.failed} reading${res.failed === 1 ? '' : 's'} not saved: see the red lines`);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save readings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.logSheet.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Utility log sheet</Typography>
                <Button data-help="maint.logSheet.save" variant="contained" startIcon={<SaveIcon />} disabled={saving || entered.length === 0} onClick={handleSave} {...helpWhy(!saving && entered.length === 0 ? { kind: 'status', message: 'Type at least one new value first.' } : null)}>
                    {saving ? 'Saving...' : `Save ${entered.length} reading${entered.length === 1 ? '' : 's'}`}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.logSheet.filter">
                <TextField
                    size="small" type="date" label="Date" value={date} sx={cell(160)} data-help="maint.logSheet.filter.date"
                    InputLabelProps={{ shrink: true }} inputProps={{ max: dhakaToday() }}
                    onChange={e => e.target.value && changeDate(e.target.value)}
                />
                <TextField
                    size="small" type="time" label="Time (Dhaka)" value={time} sx={cell(120)} data-help="maint.logSheet.filter.time"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => { setTime(e.target.value); setTimeTouched(true); }}
                />
                <TextField
                    size="small" label="Shift (optional)" value={shift} sx={cell(140)} data-help="maint.logSheet.filter.shift"
                    inputProps={{ maxLength: 20 }} onChange={e => setShift(e.target.value)}
                />
                <Box sx={cell(180)}>
                    <SearchableSelect
                        options={groups} value={category} size="small" label="Group" data-help="maint.logSheet.filter.category"
                        getOptionId={g => g.code} getOptionLabel={g => g.name}
                        onChange={code => { clearEntry(); setCategory(code as EquipmentCategory | ''); }}
                    />
                </Box>
                <Box sx={cell(200)}>
                    <SearchableSelect
                        options={types} value={typeId} size="small" label="Type" data-help="maint.logSheet.filter.type"
                        getOptionLabel={t => `${t.name} (${t.code})`} onChange={id => { clearEntry(); setTypeId(id); }}
                    />
                </Box>
                <Box sx={cell(200)}>
                    <SearchableSelect
                        options={departments} value={departmentId} size="small" label="Location" data-help="maint.logSheet.filter.location"
                        getOptionLabel={d => d.name} onChange={id => { clearEntry(); setDepartmentId(id); }}
                    />
                </Box>
            </Box>

            {sheet?.truncated && (
                <Alert severity="info" sx={{ mb: 2 }}>More meters match than the sheet can show. Narrow the filters.</Alert>
            )}

            <Paper data-help-section="maint.logSheet.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.logSheet.col.equipment">Equipment</TableCell>
                                    <TableCell data-help="maint.logSheet.col.meter">Meter</TableCell>
                                    <TableCell data-help="maint.logSheet.col.last">Last reading</TableCell>
                                    <TableCell data-help="maint.logSheet.col.recorded">Recorded on {sheet?.date ?? date}</TableCell>
                                    <TableCell data-help="maint.logSheet.col.new" sx={{ minWidth: 200 }}>New value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(sheet?.rows.length ?? 0) === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">No active meters match these filters</TableCell></TableRow>
                                )}
                                {sheet?.rows.map(r => {
                                    const raw = values[r.meterId] ?? '';
                                    const warn = rowWarning(r, raw, !!resets[r.meterId], date !== dhakaToday());
                                    const err = errors[r.meterId];
                                    return (
                                        <TableRow key={r.meterId} hover>
                                            <TableCell>
                                                <Typography variant="body2">{r.equipmentCode}</Typography>
                                                <Typography variant="caption" color="text.secondary">{r.equipmentName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2" component={RouterLink} to={`/maintenance/readings/${r.meterId}`}
                                                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                                                >
                                                    {r.meterName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {mntLabel(r.kind)}{r.unit ? `, ${r.unit}` : ''}
                                                    {r.kind === 'GAUGE' && (r.minValue != null || r.maxValue != null)
                                                        ? `, range ${fmt(r.minValue)} to ${fmt(r.maxValue)}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{fmt(r.lastValue)}</TableCell>
                                            <TableCell>
                                                {r.readings.length === 0 ? '-' : r.readings.map(x => (
                                                    <Tooltip key={x.id} title={x.recordedByName ? `by ${x.recordedByName}` : ''}>
                                                        <Chip
                                                            size="small" sx={{ mr: 0.5 }} label={`${x.value}${x.delta != null ? ` (+${x.delta})` : ''}`}
                                                            color={x.outOfRange ? 'error' : 'default'}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                    <TextField
                                                        size="small" type="number" value={raw} sx={{ flex: '1 1 110px', maxWidth: 170 }} data-help="maint.logSheet.field.value"
                                                        inputProps={{ 'aria-label': `Value for ${r.equipmentCode} ${r.meterName}`, step: 'any' }}
                                                        error={!!err || (!!warn && warn === 'Not a number')}
                                                        onChange={e => setValues(v => ({ ...v, [r.meterId]: e.target.value }))}
                                                    />
                                                    {r.kind === 'CUMULATIVE' && (
                                                        <FormControlLabel
                                                            data-help="maint.logSheet.reset"
                                                            label="Reset"
                                                            control={
                                                                <Checkbox
                                                                    size="small" checked={!!resets[r.meterId]}
                                                                    onChange={e => setResets(v => ({ ...v, [r.meterId]: e.target.checked }))}
                                                                />
                                                            }
                                                        />
                                                    )}
                                                </Box>
                                                {err && <Typography variant="caption" color="error" display="block">{err}</Typography>}
                                                {!err && warn && <Typography variant="caption" color="warning.main" display="block">{warn}</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default UtilityLogSheet;
